import { db, storage } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  setDoc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { isDemoMode } from '../demo/isDemoMode';
import demoStore from '../demo/demoStore';

const REQUIRED_DOCUMENT_TYPES = {
  VENUE_REGULATIONS: 'venue_regulations',
  LIABILITY_WAIVER: 'liability_waiver',
  RULES_OF_CONDUCT: 'rules_of_conduct',
  SAFETY_GUIDELINES: 'safety_guidelines',
  VENDOR_AGREEMENT: 'vendor_agreement',
  INSURANCE_REQUIREMENTS: 'insurance_requirements',
  OTHER: 'other'
};

const REQUIRED_DOCUMENT_TYPE_LABELS = {
  venue_regulations: 'Venue Regulations',
  liability_waiver: 'Liability Waiver',
  rules_of_conduct: 'Rules of Conduct',
  safety_guidelines: 'Safety Guidelines',
  vendor_agreement: 'Vendor Agreement',
  insurance_requirements: 'Insurance Requirements',
  other: 'Other Document'
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const reqDocsPath = (eventId) => `events/${eventId}/requiredDocuments`;
const signaturesPath = (eventId, documentId) =>
  `events/${eventId}/requiredDocuments/${documentId}/vendorSignatures`;
const acknowledgementsPath = (eventId, documentId) =>
  `events/${eventId}/requiredDocuments/${documentId}/vendorAcknowledgments`;

// ─── Upload ───────────────────────────────────────────────────────────────────

export const uploadEventRequiredDocument = async (eventId, organizerId, file, documentData) => {
  try {
    if (isDemoMode) {
      const docId = `demo-doc-${Date.now()}`;
      const DEMO_PDF_URL = 'https://mozilla.github.io/pdf.js/web/compressed.tracemonkey-pldi-09.pdf';
      demoStore.setDoc(reqDocsPath(eventId), docId, {
        title: documentData.title || file.name,
        type: documentData.type || REQUIRED_DOCUMENT_TYPES.OTHER,
        description: documentData.description || '',
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        storagePath: `event-required-documents/${eventId}/${file.name}`,
        downloadURL: DEMO_PDF_URL,
        requiresSignature: documentData.requiresSignature || false,
        requiresAcknowledgment: documentData.requiresAcknowledgment !== false,
        isRequired: documentData.isRequired !== false,
        organizerId,
        signatureFields: [],
        assignedVendorIds: [],
        signingStatus: null,
        signingDeadline: null,
        uploadedAt: new Date(),
        updatedAt: new Date(),
      });
      return { success: true, documentId: docId, message: 'Required document added successfully' };
    }

    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storagePath = `event-required-documents/${eventId}/${fileName}`;

    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    const docRef = await addDoc(collection(db, 'events', eventId, 'requiredDocuments'), {
      title: documentData.title || file.name,
      type: documentData.type || REQUIRED_DOCUMENT_TYPES.OTHER,
      description: documentData.description || '',
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      storagePath,
      downloadURL,
      requiresSignature: documentData.requiresSignature || false,
      requiresAcknowledgment: documentData.requiresAcknowledgment !== false,
      isRequired: documentData.isRequired !== false,
      organizerId,
      uploadedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return {
      success: true,
      documentId: docRef.id,
      message: 'Required document added successfully'
    };
  } catch (error) {
    console.error('Error uploading event required document:', error);
    return { success: false, error: error.message };
  }
};

// ─── Read ─────────────────────────────────────────────────────────────────────

export const getEventRequiredDocuments = async (eventId) => {
  try {
    if (isDemoMode) {
      const docs = demoStore.getDocs(reqDocsPath(eventId));
      const documents = docs
        .map(d => ({ id: d.id, ...d.data }))
        .sort((a, b) => {
          const at = a.uploadedAt instanceof Date ? a.uploadedAt : new Date(a.uploadedAt || 0);
          const bt = b.uploadedAt instanceof Date ? b.uploadedAt : new Date(b.uploadedAt || 0);
          return bt - at;
        });
      return { success: true, documents };
    }

    const docsRef = collection(db, 'events', eventId, 'requiredDocuments');
    const q = query(docsRef, orderBy('uploadedAt', 'desc'));
    const snapshot = await getDocs(q);

    const documents = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, documents };
  } catch (error) {
    console.error('Error fetching event required documents:', error);
    return { success: false, documents: [], error: error.message };
  }
};

export const getEventRequiredDocument = async (eventId, documentId) => {
  try {
    if (isDemoMode) {
      const data = demoStore.getDoc(reqDocsPath(eventId), documentId);
      if (!data) return { success: false, error: 'Document not found' };
      return { success: true, document: { id: documentId, ...data } };
    }

    const docRef = doc(db, 'events', eventId, 'requiredDocuments', documentId);
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) {
      return { success: false, error: 'Document not found' };
    }

    return {
      success: true,
      document: {
        id: snapshot.id,
        ...snapshot.data()
      }
    };
  } catch (error) {
    console.error('Error fetching event required document:', error);
    return { success: false, error: error.message };
  }
};

// ─── Update ───────────────────────────────────────────────────────────────────

export const updateEventRequiredDocument = async (eventId, documentId, updates) => {
  try {
    if (isDemoMode) {
      demoStore.updateDoc(reqDocsPath(eventId), documentId, { ...updates, updatedAt: new Date() });
      return { success: true, message: 'Document updated successfully' };
    }

    const docRef = doc(db, 'events', eventId, 'requiredDocuments', documentId);
    await updateDoc(docRef, { ...updates, updatedAt: serverTimestamp() });
    return { success: true, message: 'Document updated successfully' };
  } catch (error) {
    console.error('Error updating event required document:', error);
    return { success: false, error: error.message };
  }
};

// ─── Delete ───────────────────────────────────────────────────────────────────

export const deleteEventRequiredDocument = async (eventId, documentId) => {
  try {
    if (isDemoMode) {
      demoStore.deleteDoc(reqDocsPath(eventId), documentId);
      return { success: true, message: 'Document deleted successfully' };
    }

    const docRef = doc(db, 'events', eventId, 'requiredDocuments', documentId);
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) {
      return { success: false, error: 'Document not found' };
    }

    const data = snapshot.data();
    if (data.storagePath) {
      try {
        const storageRef = ref(storage, data.storagePath);
        await deleteObject(storageRef);
      } catch (storageError) {
        console.warn('Could not delete file from storage:', storageError);
      }
    }

    await deleteDoc(docRef);
    return { success: true, message: 'Document deleted successfully' };
  } catch (error) {
    console.error('Error deleting event required document:', error);
    return { success: false, error: error.message };
  }
};

// ─── Acknowledgments ──────────────────────────────────────────────────────────

export const acknowledgeDocument = async (eventId, documentId, vendorId, vendorName, signatureData = null) => {
  try {
    if (isDemoMode) {
      demoStore.setDoc(acknowledgementsPath(eventId, documentId), vendorId, {
        vendorId,
        vendorName: vendorName || 'Unknown Vendor',
        acknowledgedAt: new Date(),
        signature: signatureData || null,
        ipAddress: null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });
      return { success: true, message: 'Document acknowledged successfully' };
    }

    const ackRef = doc(db, 'events', eventId, 'requiredDocuments', documentId, 'vendorAcknowledgments', vendorId);
    await setDoc(ackRef, {
      vendorId,
      vendorName: vendorName || 'Unknown Vendor',
      acknowledgedAt: serverTimestamp(),
      signature: signatureData || null,
      ipAddress: null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null
    });

    return { success: true, message: 'Document acknowledged successfully' };
  } catch (error) {
    console.error('Error acknowledging document:', error);
    return { success: false, error: error.message };
  }
};

export const getVendorAcknowledgment = async (eventId, documentId, vendorId) => {
  try {
    if (isDemoMode) {
      const data = demoStore.getDoc(acknowledgementsPath(eventId, documentId), vendorId);
      if (!data) return { success: true, acknowledged: false };
      return { success: true, acknowledged: true, acknowledgment: data };
    }

    const ackRef = doc(db, 'events', eventId, 'requiredDocuments', documentId, 'vendorAcknowledgments', vendorId);
    const snapshot = await getDoc(ackRef);

    if (!snapshot.exists()) {
      return { success: true, acknowledged: false };
    }

    return {
      success: true,
      acknowledged: true,
      acknowledgment: snapshot.data()
    };
  } catch (error) {
    console.error('Error checking vendor acknowledgment:', error);
    return { success: false, acknowledged: false, error: error.message };
  }
};

export const getDocumentAcknowledgments = async (eventId, documentId) => {
  try {
    if (isDemoMode) {
      const docs = demoStore.getDocs(acknowledgementsPath(eventId, documentId));
      const acknowledgments = docs.map(d => ({ vendorId: d.id, ...d.data }));
      return { success: true, acknowledgments };
    }

    const acksRef = collection(db, 'events', eventId, 'requiredDocuments', documentId, 'vendorAcknowledgments');
    const snapshot = await getDocs(acksRef);

    const acknowledgments = snapshot.docs.map(doc => ({
      vendorId: doc.id,
      ...doc.data()
    }));

    return { success: true, acknowledgments };
  } catch (error) {
    console.error('Error fetching document acknowledgments:', error);
    return { success: false, acknowledgments: [], error: error.message };
  }
};

// ─── Compliance ───────────────────────────────────────────────────────────────

export const getVendorDocumentComplianceForEvent = async (eventId, vendorId) => {
  try {
    const requiredDocsResult = await getEventRequiredDocuments(eventId);
    if (!requiredDocsResult.success) return requiredDocsResult;

    const requiredDocs = requiredDocsResult.documents.filter(d => d.isRequired);
    const compliance = [];

    for (const reqDoc of requiredDocs) {
      const ackResult = await getVendorAcknowledgment(eventId, reqDoc.id, vendorId);
      compliance.push({
        document: reqDoc,
        acknowledged: ackResult.acknowledged,
        acknowledgment: ackResult.acknowledgment || null
      });
    }

    const allAcknowledged = compliance.every(c => c.acknowledged);
    const acknowledgedCount = compliance.filter(c => c.acknowledged).length;

    return {
      success: true,
      compliance,
      allAcknowledged,
      acknowledgedCount,
      totalRequired: requiredDocs.length,
      isCompliant: allAcknowledged
    };
  } catch (error) {
    console.error('Error checking vendor document compliance for event:', error);
    return { success: false, isCompliant: false, error: error.message };
  }
};

// ─── Document Signing (Pro feature) ──────────────────────────────────────────

/**
 * Save signature field positions on a document and assign vendors.
 * signatureFields: [{ id, type, page, x, y, width, height, label, required }]
 * Coordinates are stored as percentages (0–100) for resolution-independence.
 */
export const updateDocumentSignatureFields = async (
  eventId,
  documentId,
  signatureFields,
  assignedVendorIds = [],
  signingDeadline = null
) => {
  try {
    const updates = {
      signatureFields,
      assignedVendorIds,
      signingDeadline: signingDeadline || null,
      signingStatus: assignedVendorIds.length > 0 ? 'pending_signatures' : 'draft',
      updatedAt: isDemoMode ? new Date() : serverTimestamp(),
    };

    if (isDemoMode) {
      demoStore.updateDoc(reqDocsPath(eventId), documentId, updates);
      return { success: true };
    }

    const docRef = doc(db, 'events', eventId, 'requiredDocuments', documentId);
    await updateDoc(docRef, { ...updates, updatedAt: serverTimestamp() });
    return { success: true };
  } catch (error) {
    console.error('Error updating document signature fields:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all vendor signatures for a document.
 */
export const getVendorSignatures = async (eventId, documentId) => {
  try {
    if (isDemoMode) {
      const docs = demoStore.getDocs(signaturesPath(eventId, documentId));
      const signatures = docs.map(d => ({ vendorId: d.id, ...d.data }));
      return { success: true, signatures };
    }

    const sigsRef = collection(db, 'events', eventId, 'requiredDocuments', documentId, 'vendorSignatures');
    const snapshot = await getDocs(sigsRef);
    const signatures = snapshot.docs.map(d => ({ vendorId: d.id, ...d.data() }));
    return { success: true, signatures };
  } catch (error) {
    console.error('Error fetching vendor signatures:', error);
    return { success: false, signatures: [], error: error.message };
  }
};

/**
 * Get a single vendor's signature record.
 */
export const getVendorSignature = async (eventId, documentId, vendorId) => {
  try {
    if (isDemoMode) {
      const data = demoStore.getDoc(signaturesPath(eventId, documentId), vendorId);
      if (!data) return { success: true, signed: false };
      return { success: true, signed: true, signature: data };
    }

    const sigRef = doc(db, 'events', eventId, 'requiredDocuments', documentId, 'vendorSignatures', vendorId);
    const snapshot = await getDoc(sigRef);
    if (!snapshot.exists()) return { success: true, signed: false };
    return { success: true, signed: true, signature: snapshot.data() };
  } catch (error) {
    console.error('Error fetching vendor signature:', error);
    return { success: false, signed: false, error: error.message };
  }
};

/**
 * Vendor submits signatures for all fields on a document.
 * signaturesMap: { [fieldId]: { type, dataURL } }
 * The generateSignedPDF Cloud Function is called separately after this succeeds.
 */
export const submitVendorSignatures = async (
  eventId,
  documentId,
  vendorId,
  vendorName,
  signaturesMap
) => {
  try {
    const signatures = {};
    for (const [fieldId, sig] of Object.entries(signaturesMap)) {
      signatures[fieldId] = {
        type: sig.type,
        dataURL: sig.dataURL,
        signedAt: new Date(),
      };
    }

    const record = {
      vendorId,
      vendorName: vendorName || 'Unknown Vendor',
      status: 'signed',
      signatures,
      completedAt: new Date(),
      signedPdfStoragePath: null,
      signedPdfDownloadURL: null,
      ipAddress: null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    };

    if (isDemoMode) {
      demoStore.setDoc(signaturesPath(eventId, documentId), vendorId, record);
      return { success: true };
    }

    const sigRef = doc(db, 'events', eventId, 'requiredDocuments', documentId, 'vendorSignatures', vendorId);
    await setDoc(sigRef, {
      ...record,
      signatures: Object.fromEntries(
        Object.entries(signaturesMap).map(([fieldId, sig]) => [
          fieldId,
          { type: sig.type, dataURL: sig.dataURL, signedAt: serverTimestamp() },
        ])
      ),
      completedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error submitting vendor signatures:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update the signed PDF URL on a vendor signature record after generation.
 */
export const updateVendorSignedPdfUrl = async (
  eventId,
  documentId,
  vendorId,
  signedPdfStoragePath,
  signedPdfDownloadURL
) => {
  try {
    if (isDemoMode) {
      demoStore.updateDoc(signaturesPath(eventId, documentId), vendorId, {
        signedPdfStoragePath,
        signedPdfDownloadURL,
      });
      return { success: true };
    }

    const sigRef = doc(db, 'events', eventId, 'requiredDocuments', documentId, 'vendorSignatures', vendorId);
    await updateDoc(sigRef, { signedPdfStoragePath, signedPdfDownloadURL });
    return { success: true };
  } catch (error) {
    console.error('Error updating signed PDF URL:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all documents pending signature for a vendor across a list of event IDs.
 */
export const getPendingSigningDocumentsForVendor = async (vendorId, eventIds = []) => {
  try {
    const pending = [];

    for (const eventId of eventIds) {
      const docsResult = await getEventRequiredDocuments(eventId);
      if (!docsResult.success) continue;

      for (const reqDoc of docsResult.documents) {
        if (
          reqDoc.requiresSignature &&
          reqDoc.signingStatus === 'pending_signatures' &&
          (reqDoc.assignedVendorIds?.includes(vendorId) || reqDoc.assignedVendorIds?.length === 0)
        ) {
          const sigResult = await getVendorSignature(eventId, reqDoc.id, vendorId);
          if (!sigResult.signed) {
            pending.push({ eventId, document: reqDoc });
          }
        }
      }
    }

    return { success: true, pending };
  } catch (error) {
    console.error('Error fetching pending signing documents:', error);
    return { success: false, pending: [], error: error.message };
  }
};

export { REQUIRED_DOCUMENT_TYPES, REQUIRED_DOCUMENT_TYPE_LABELS };
